import React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Button } from '@origo/origo-ui';
import { ArrowDownIcon } from '@phosphor-icons/react';
import type { RelationPair, RelationParty } from '../../../types';
import styles from './RelationCard.module.css';

interface RelationCardProps {
    pair: RelationPair;
    onEdit: (id: string) => void;
    onDeleteRequest: (id: string) => void;
}

const Party: React.FC<{ party: RelationParty }> = ({ party }) => {
    const { t } = useTranslation();
    return (
        <div className={styles.party}>
            <span className={styles.partyName}>{party.plannavn}</span>
            {/* Prikken ligger i samme span som ID-en, så den følger med ved linjeskift. */}
            <span className={styles.partyId}>
                <span className={styles.partySeparator} aria-hidden>·</span>
                {t('relations.planId', { planId: party.planId })}
            </span>
        </div>
    );
};

const RelationCard: React.FC<RelationCardProps> = ({ pair, onEdit, onDeleteRequest }) => {
    const { t } = useTranslation();

    return (
        <article className={styles.card}>
            {/*
               Relasjonen leses ovenfra og ned som en setning: "Bjerkealleen 26 /
               blir overstyrt av / Adkomstvei …". Loddrett stabling gjør at lange
               plannavn aldri bryter setningen midt i (pil og verb havner ikke
               alene på slutten av en linje). Skinnen til venstre binder partene
               sammen; verbet er chippen på skinnen.
            */}
            <div
                role="group"
                className={styles.relation}
                aria-label={t('relations.relationFlow', { from: pair.from.plannavn, to: pair.to.plannavn })}
            >
                <Party party={pair.from} />
                <div className={styles.connector}>
                    <Chip
                        variant={pair.type.variant ?? 'info'}
                        icon={<ArrowDownIcon size={14} weight="bold" aria-hidden />}
                    >
                        {pair.type.label}
                    </Chip>
                </div>
                <Party party={pair.to} />
            </div>
            {pair.begrunnelse && <p className={styles.begrunnelse}>{pair.begrunnelse}</p>}
            <div className={styles.metaRow}>
                <span className={styles.meta}>
                    {t('relations.vedtakEndelig')}: {pair.vedtakEndeligPlanDato}
                </span>
                <div className={styles.actions}>
                    <Button variant="danger" onClick={() => onDeleteRequest(pair.id)}>
                        {t('relations.delete.action')}
                    </Button>
                    <Button variant="secondary" onClick={() => onEdit(pair.id)}>
                        {t('common.edit')}
                    </Button>
                </div>
            </div>
        </article>
    );
};

export default RelationCard;
